import { type ActorRefFrom, assertEvent, assign, sendParent, setup } from "xstate";

import type { PlayerId } from "@/types/branded";
import type { MatchId } from "../types";
import type { SwissGraph } from "../types/graph";
import type { SwissNode } from "../types/node";
import type { SwissParticipantStats } from "../types/participantStat";

import { calculateNextRound, createInitialStandings, updatePlayerStats } from "./swissUtils";

export const swissMatchMachine = setup({
  types: {
    context: {} as {
      node: SwissNode;
      players: [PlayerId | null | undefined, PlayerId | null | undefined];
      winner: PlayerId | "draw" | undefined;
    },
    input: {} as { node: SwissNode },
    events: {} as
      | { type: "ASSIGN_PLAYER"; playerId: PlayerId | null; slot: 0 | 1 }
      | { type: "REPORT_RESULT"; winnerId: PlayerId | "draw" },
  },
}).createMachine({
  id: "match",
  initial: "waiting",
  context: ({ input }) => ({
    node: input.node,
    players: [undefined, undefined],
    winner: undefined,
  }),
  states: {
    waiting: {
      always: {
        target: "ready",
        guard: ({ context }) => context.players.every((p) => p !== undefined),
      },
      on: {
        ASSIGN_PLAYER: {
          actions: assign({
            players: ({ context, event }) => {
              const newPlayers = [...context.players] as [PlayerId | null, PlayerId | null];
              newPlayers[event.slot] = event.playerId;
              return newPlayers;
            },
          }),
        },
      },
    },
    ready: {
      always: {
        target: "completed",
        guard: ({ context }) => context.players.includes(null),
      },
      on: {
        REPORT_RESULT: {
          actions: assign({ winner: ({ event }) => event.winnerId }),
          target: "completed",
        },
      },
    },
    completed: {
      type: "final",
      entry: sendParent(
        ({ context }) =>
          ({
            type: "MATCH_FINISHED",
            matchId: context.node.id,
            winnerId: context.winner ?? context.players.find((p) => p !== null) ?? "draw",
            players: context.players as [PlayerId | null, PlayerId | null],
          }) satisfies SwissMatchFinishedEvent,
      ),
    },
  },
});

export type SwissMatchActorRef = ActorRefFrom<typeof swissMatchMachine>;

export type SwissContext = {
  players: PlayerId[];
  standings: Map<PlayerId, SwissParticipantStats>;
  graph: SwissGraph;
  matchRefs: Map<MatchId, SwissMatchActorRef>;
  currentRound: number;
  maxRounds: number;
  pendingMatches: Set<MatchId>;
};

export type SwissMatchFinishedEvent = {
  type: "MATCH_FINISHED";
  matchId: MatchId;
  winnerId: PlayerId | "draw";
  players: [PlayerId | null, PlayerId | null];
};

export const swissMachine = setup({
  types: {
    context: {} as SwissContext,
    events: {} as SwissMatchFinishedEvent,
    input: {} as { players: PlayerId[]; maxRounds?: number },
    output: [] as PlayerId[][],
  },
  actors: { matchActor: swissMatchMachine },
  actions: {
    spawnMatches: assign(({ context, spawn }) => {
      const newRefs = new Map(context.matchRefs);
      const newPending = new Set(context.pendingMatches);

      context.graph.nodes.forEach((node, id) => {
        if (!newRefs.has(id)) {
          const ref = spawn("matchActor", { id, input: { node } });

          newRefs.set(id, ref);
          newPending.add(id);
        }
      });
      return { matchRefs: newRefs, pendingMatches: newPending };
    }),

    applySeeds: ({ context }) => {
      context.graph.edges.forEach((edge) => {
        if (edge.kind === "seed") {
          const targetRef = context.matchRefs.get(edge.to);

          if (targetRef && targetRef.getSnapshot().matches("waiting")) {
            targetRef.send({ type: "ASSIGN_PLAYER", playerId: edge.player, slot: edge.slot });
          }
        }
      });
    },

    updateStandings: assign(({ context, event }) => {
      assertEvent(event, "MATCH_FINISHED");

      const newPending = new Set(context.pendingMatches);
      newPending.delete(event.matchId);

      return { standings: updatePlayerStats(context.standings, event), pendingMatches: newPending };
    }),

    generateRound: assign(({ context }) => {
      const { nodes, edges } = calculateNextRound(context.standings, context.currentRound + 1);

      return {
        graph: {
          nodes: new Map([...context.graph.nodes, ...nodes.map((n) => [n.id, n] as const)]),
          edges: [...context.graph.edges, ...edges],
        },
        currentRound: context.currentRound + 1,
      };
    }),

    calculateTieBreakers: assign(({ context }) => {
      const finalStandings = new Map(context.standings);
      for (const [_, stats] of finalStandings) {
        stats.buchholz = stats.opponents.reduce(
          (acc, oppId) => acc + (context.standings.get(oppId)?.points || 0),
          0,
        );
      }
      return { standings: finalStandings };
    }),
  },

  guards: {
    isRoundComplete: ({ context }) => context.pendingMatches.size === 0,
    isFinished: ({ context }) => {
      return context.currentRound >= context.maxRounds && context.pendingMatches.size === 0;
    },
  },
}).createMachine({
  id: "tournamentManager",
  initial: "initializing",
  context: ({ input }) => ({
    players: input.players,
    maxRounds: input.maxRounds ?? Math.ceil(Math.log2(input.players.length)),
    standings: createInitialStandings(input.players),
    graph: { nodes: new Map(), edges: [] },
    matchRefs: new Map(),
    currentRound: 0, // generateRound will increment this to 1 for the first round
    pendingMatches: new Set(),
  }),

  states: {
    initializing: {
      always: "pairing",
    },

    playing: {
      on: {
        MATCH_FINISHED: {
          actions: ["updateStandings"],
          target: "evaluating",
        },
      },
    },

    evaluating: {
      always: [
        { target: "finished", guard: "isFinished" },
        { target: "pairing", guard: "isRoundComplete" },
        { target: "playing" },
      ],
    },

    pairing: {
      entry: ["generateRound", "spawnMatches", "applySeeds"],
      always: "playing",
    },

    finished: {
      entry: ["calculateTieBreakers"],
      type: "final",
    },
  },

  output: ({ context }) => {
    const tiersByScore = new Map<number, PlayerId[]>();
    const sorted = Array.from(context.standings.values()).sort(
      (a, b) => b.points - a.points || b.buchholz - a.buchholz,
    );

    for (const stats of sorted) {
      if (!tiersByScore.has(stats.points)) tiersByScore.set(stats.points, []);
      tiersByScore.get(stats.points)!.push(stats.id);
    }

    return Array.from(tiersByScore.values());
  },
});
